/**
 * Portrait-only "More" control: the actions that don't earn a slot in the
 * bottom bar live in a modal bottom sheet. WCAG AAA: 48px targets, 7:1 text
 * contrast, focus-visible rings, Esc/backdrop close, focus restored on close.
 * @module MoreControl
 * @category Display Components
 */
import { text } from "@quickpoint.me/shared";
import { Suspense, useEffect, useRef, useState } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import { Authorization } from "../../state/authAtoms";
import { IsInPortrait } from "../../state/selectors";
import { DemoMode } from "../../state/uiAtoms";
import { LocaleAtom } from "../../state/localeAtom";
import { TopBarControl } from "../StyledControl";
import FilesControl from "./FilesControl";
import LogoutControl from "./LogOutControl";
import PublishControl from "./PublishControl";
import PublishControlFallBack from "./PublishControlFallBack";
import ReadControl from "./ReadControl";
import SignInFromDemoControl from "./SignInFromDemoControl";
import TknsControl from "./TknsControl";
import TourControl from "./TourControl";

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10000;
`;

const Sheet = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 10001;
  background: ${(props) => props.theme.navigationBackGround};
  border-top: 1px solid ${(props) => props.theme.navigationFrame};
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  padding: 0.5rem 0.5rem calc(0.5rem + env(safe-area-inset-bottom, 0px));
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  /* the reused TopBarControl buttons become full-width 48px list rows with
     visible edges (border ≥3:1 on both fills, WCAG 1.4.11) */
  button {
    width: 100%;
    min-height: 48px;
    height: 48px;
    margin: 0;
    justify-content: flex-start;
    text-align: left;
    font-size: 1rem;
    background: #fff;
    border: 1.5px solid #6E6A7A;
    border-radius: 10px;
  }
`;

const SheetTitle = styled.h2`
  margin: 0.25rem 0.75rem 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #333;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

function MoreControl() {
  const [open, setOpen] = useState(false);
  const isInPortrait = useRecoilValue(IsInPortrait);
  const demoMode = useRecoilValue(DemoMode);
  const isLoggedIn = useRecoilValue(Authorization).isLoggedIn;
  useRecoilValue(LocaleAtom); // re-render on locale change
  const triggerRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return;
    // focus the first action so keyboard/screen-reader users land in the sheet
    const firstButton = sheetRef.current?.querySelector("button");
    (firstButton as HTMLButtonElement | undefined)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open]);

  return (
    <>
      <TopBarControl
        ref={triggerRef}
        $isInPortrait={isInPortrait}
        data-testid="more-control"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={text.controls.moreOptions}
      >
        {text.controls.more}
      </TopBarControl>
      {open && (
        <>
          <Backdrop onClick={close} data-testid="more-sheet-backdrop" />
          <Sheet
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={text.controls.moreOptions}
            data-testid="more-sheet"
            // any action both does its job and dismisses the sheet
            onClickCapture={(e) => {
              if ((e.target as HTMLElement).closest("button")) {
                setTimeout(() => setOpen(false), 0);
              }
            }}
          >
            <SheetTitle>{text.controls.moreOptions}</SheetTitle>
            <FilesControl />
            <Suspense fallback={<PublishControlFallBack />}>
              <PublishControl />
            </Suspense>
            <ReadControl />
            <TknsControl />
            <TourControl />
            {!demoMode && isLoggedIn && <LogoutControl />}
            {demoMode && !isLoggedIn && <SignInFromDemoControl />}
          </Sheet>
        </>
      )}
    </>
  );
}

export default MoreControl;
